import React, { Component, ReactNode, CSSProperties, RefObject } from 'react'

import classNames from 'classnames'

import 'perfect-scrollbar/css/perfect-scrollbar.css'
import PerfectScrollbar from 'perfect-scrollbar'

import './perfect-scrollbar.scss'

interface HandlerNameByEvent {
  [key: string]: string
}

const handlerNameByEvent: HandlerNameByEvent = {
  'ps-scroll-y': 'onScrollY',
  'ps-scroll-x': 'onScrollX',
  'ps-scroll-up': 'onScrollUp',
  'ps-scroll-down': 'onScrollDown',
  'ps-scroll-left': 'onScrollLeft',
  'ps-scroll-right': 'onScrollRight',
  'ps-y-reach-start': 'onYReachStart',
  'ps-y-reach-end': 'onYReachEnd',
  'ps-x-reach-start': 'onXReachStart',
  'ps-x-reach-end': 'onXReachEnd',
}
Object.freeze(handlerNameByEvent)

interface ScrollBarProps {
  className?: string
  style?: CSSProperties
  options?: any
  refScroll?:
    | ((ps: PerfectScrollbar | null) => void)
    | RefObject<PerfectScrollbar | null>
    | RefObject<any>
  onScrollY?: (event: Event, ps: PerfectScrollbar) => void
  onScrollX?: (event: Event, ps: PerfectScrollbar) => void
  onScrollUp?: (event: Event, ps: PerfectScrollbar) => void
  onScrollDown?: (event: Event, ps: PerfectScrollbar) => void
  onScrollLeft?: (event: Event, ps: PerfectScrollbar) => void
  onScrollRight?: (event: Event, ps: PerfectScrollbar) => void
  onYReachStart?: (event: Event, ps: PerfectScrollbar) => void
  onYReachEnd?: (event: Event, ps: PerfectScrollbar) => void
  onXReachStart?: (event: Event, ps: PerfectScrollbar) => void
  onXReachEnd?: (event: Event, ps: PerfectScrollbar) => void
  height?: string | number
  component?: string | React.ComponentType<any>
  always?: boolean
  thumbHover?: boolean
  children?: ReactNode
  [key: string]: any
}

interface ResizeObserverInfo {
  observer?: ResizeObserver
  element?: HTMLElement | null
  resizeTimer?: NodeJS.Timeout
  callUpdate?: (delay?: number) => void
  clear?: () => void
}

class ScrollBar extends Component<ScrollBarProps> {
  _ps: PerfectScrollbar | null = null
  _handlerByEvent: Record<string, EventListener> = {}
  hasSize: boolean = false
  _container: HTMLElement | null = null
  resizeObserver: ResizeObserverInfo = {}
  updateScrollTimer: NodeJS.Timeout | null = null

  constructor(props: ScrollBarProps) {
    super(props)

    this.handleRef = this.handleRef.bind(this)
    // this._handlerByEvent = {};
  }

  componentDidMount() {
    this.createPS()

    this.checkContainerResize()
  }

  componentDidUpdate(prevProps: ScrollBarProps) {
    this._updateEventHook(prevProps)

    this.updateScroll()

    if (prevProps.className !== this.props.className) {
      this._updateClassName()
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver.clear != null) {
      this.resizeObserver.clear()
    }

    this.deletePS()
  }

  checkContainerResize = (): void => {
    if (this._container != null) {
      if (this.resizeObserver == null || this.resizeObserver.observer == null) {
        this.resizeObserver = {}

        this.resizeObserver.callUpdate = (delay: number = 300) => {
          if (this.resizeObserver.resizeTimer != null) {
            clearTimeout(this.resizeObserver.resizeTimer)
          }
          this.resizeObserver.resizeTimer = setTimeout(() => {
            if (this._ps != null && this.hasSize === true) {
              this.updateScroll()
            } else {
              this.createPS()
            }
            // console.log(' callUpdate - ', this._ps);
          }, delay)
        }

        this.resizeObserver.observer = new ResizeObserver((e: ResizeObserverEntry[]) => {
          console.log(' resize scrollContainer - ', e)
          if (this.resizeObserver.callUpdate) {
            this.resizeObserver.callUpdate()
          }
        })

        this.resizeObserver.clear = () => {
          const { observer, element, resizeTimer } = this.resizeObserver
          if (observer != null && element != null) {
            observer.disconnect()
            if (resizeTimer != null) {
              clearTimeout(resizeTimer)
            }
          }
        }
      }

      if (this.resizeObserver.element !== this._container) {
        if (this.resizeObserver.clear) {
          this.resizeObserver.clear()
        }

        this.resizeObserver.element = this._container

        if (this.resizeObserver.element != null && this.resizeObserver.observer != null) {
          if (this.resizeObserver.callUpdate) {
            this.resizeObserver.callUpdate(0)
          }
          this.resizeObserver.observer.observe(this.resizeObserver.element, {
            box: 'border-box' as ResizeObserverBoxOptions,
          })

          const parent = this.resizeObserver.element
          if (parent.children.length > 0 && parent.children.length === parent.childNodes.length) {
            // perfect-scrollbar의 UI가 아닌 노드가 하나면, 그 노드의 크기가 content의 크기임.
            const contentList = Array.from(parent.children).filter((node: Element) => {
              return node.className.indexOf('ps__rail-') < 0
            })

            if (contentList.length === 1) {
              this.resizeObserver.observer.observe(contentList[0], {
                box: 'border-box' as ResizeObserverBoxOptions,
              })
            }
          }
        }
      }
    }
  }

  createPS = (): void => {
    if (this._ps != null) {
      this.deletePS()
    }
    if (this._container != null) {
      this.hasSize = this._container.offsetWidth > 0 && this._container.offsetHeight > 0

      const options = {
        scrollYMarginOffset: 1,
        ...(this.props.options || {}),
      }

      this._ps = new PerfectScrollbar(this._container, options)
      // hook up events
      this._updateEventHook()
      this._updateClassName()

      const refType = typeof this.props.refScroll
      if (refType === 'function') {
        ;(this.props.refScroll as (ps: PerfectScrollbar | null) => void)(this._ps)
      } else if (refType === 'object' && this.props.refScroll) {
        ;(this.props.refScroll as React.MutableRefObject<PerfectScrollbar | null>).current =
          this._ps
      }

      this.updateScrollTimer = setTimeout(() => {
        this.updateScroll()
        this.updateScrollTimer = null
      }, 300)
    }
  }

  deletePS = (): void => {
    if (this.updateScrollTimer != null) {
      clearTimeout(this.updateScrollTimer)
      this.updateScrollTimer = null
    }

    // unhook up evens
    Object.keys(this._handlerByEvent).forEach((key) => {
      const value = this._handlerByEvent[key]

      if (value && this._container) {
        this._container.removeEventListener(key, value, false)
      }
    })
    this._handlerByEvent = {}
    if (this._ps) {
      this._ps.destroy()
      this._ps = null
    }
  }

  getProps = (): ScrollBarProps => {
    return {
      className: '',
      style: undefined,
      options: undefined,
      refScroll: () => {},
      onScrollY: undefined,
      onScrollX: undefined,
      onScrollUp: undefined,
      onScrollDown: undefined,
      onScrollLeft: undefined,
      onScrollRight: undefined,
      onYReachStart: undefined,
      onYReachEnd: undefined,
      onXReachStart: undefined,
      onXReachEnd: undefined,
      height: undefined,
      component: 'div',
      always: false,
      thumbHover: false,
      ...this.props,
    }
  }

  _updateEventHook(prevProps: Partial<ScrollBarProps> = {}): void {
    // hook up events
    Object.keys(handlerNameByEvent).forEach((key) => {
      const handlerName = handlerNameByEvent[key] as keyof ScrollBarProps
      const callback = this.props[handlerName] as
        | ((event: Event, ps: PerfectScrollbar) => void)
        | undefined
      const prevCallback = prevProps[handlerName] as
        | ((event: Event, ps: PerfectScrollbar) => void)
        | undefined
      if (callback !== prevCallback) {
        if (prevCallback && this._container) {
          const prevHandler = this._handlerByEvent[key]
          this._container.removeEventListener(key, prevHandler, false)
          this._handlerByEvent[key] = null as any
        }
        if (callback && this._container && this._ps) {
          const handler = (e: Event) => callback(e, this._ps!)
          this._container.addEventListener(key, handler, false)
          this._handlerByEvent[key] = handler
        }
      }
    })
  }

  _updateClassName(): void {
    const { always, thumbHover } = this.getProps()

    if (!this._container) return

    const psClassNames = this._container.className
      .split(' ')
      .filter((name) => name.match(/^ps([-_].+|)$/))
      .join(' ')

    const clsNames = ['scroll-content']
    if (psClassNames != null && psClassNames.trim().length > 0) {
      clsNames.push(psClassNames)
    }
    if (always === true) {
      clsNames.push('always')
    }
    if (thumbHover === true) {
      clsNames.push('thumb-hover')
    }

    if (this._container) {
      this._container.className = clsNames.join(' ')
    }
  }

  updateScroll(): void {
    if (this._ps != null) {
      this._ps.update()
    }
  }

  handleRef(ref: HTMLElement | null): void {
    if (ref != null) {
      this._container = ref
      this.createPS()
    }
  }

  render() {
    const {
      className,
      style,
      options,
      refScroll,
      onScrollY,
      onScrollX,
      onScrollUp,
      onScrollDown,
      onScrollLeft,
      onScrollRight,
      onYReachStart,
      onYReachEnd,
      onXReachStart,
      onXReachEnd,
      component,
      children,
      height,
      always,
      thumbHover,
      ...remainProps
    } = this.getProps()

    const Comp = component as any

    const newStyle: CSSProperties = { ...style }
    if (height != null) {
      newStyle.height = height
    }

    return (
      <div className={classNames('perfect-list', className)} style={newStyle}>
        <Comp ref={this.handleRef} {...remainProps}>
          {children}
        </Comp>
      </div>
    )

    // return (
    //   <Comp ref={this.handleRef} {...remainProps} style={newStyle}>
    //     {children}
    //   </Comp>
    // );
  }
}

export default ScrollBar
